import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useGenErrorMsgBanners } from "../useGenErrorMsgBanners";

/**
 * GET /api/aiAnnotation/baseTaxonomy.
 *
 * Server-side code module (no longer a per-tenant platformConfigs entry) that
 * serves the base annotation taxonomy:
 *   {
 *     thematics: [{ title, description, allowNewTags, tags: [{ title, description, category }] }],
 *     attributeCategories: [{ key, display, description, selectMode, enabled, tags: [{ display, description }] }]
 *   }
 *
 * Requires auth; axios baseURL already targets /api.
 */
const getBaseTaxonomy = async () => {
  const response = await axios.get("/api/aiAnnotation/baseTaxonomy");
  return response.data;
};

export const useGetBaseTaxonomy = () =>
  useQuery({
    queryKey: ["baseTaxonomy"],
    queryFn: getBaseTaxonomy,
  });

/**
 * GET /api/aiAnnotation/promptOverridesManifest.
 *
 * Server-generated manifest of overridable split-mode prompt blocks. Each
 * entry describes one block's tab assignment, display name, description,
 * LangChain placeholders (which MUST be preserved in an override), and
 * defaultContent (the in-source default the tenant can start from).
 *
 * Used by the PlatformConfigEditor's "Prompts" tab to render one Lexical
 * editor per block. See ai/dualAgentAnnotation/agents/annotator/prompts/
 * splitPromptOverrides.js on the BE for the source of truth.
 */
const getPromptOverridesManifest = async () => {
  const response = await axios.get("/api/aiAnnotation/promptOverridesManifest");
  return response.data;
};

export const useGetPromptOverridesManifest = () =>
  useQuery({
    queryKey: ["promptOverridesManifest"],
    queryFn: getPromptOverridesManifest,
    staleTime: Infinity, // manifest is code-generated, never changes at runtime
  });

/**
 * GET /api/aiAnnotation/getTopicClusteringTree?topicClusteringLens=<lens>.
 *
 * The tenant's observation-clustering tree (split-mode): bucket nodes →
 * finding leaves, each node { nodeId, label, description, annotationIds,
 * observations, ... }, plus unplacedObservations + signalMetaById. Returns
 * null (204-shaped) when no recluster has run yet for the lens.
 *
 * Shared by the Topics tab (TopicClusteringTreeView) and the Topics filter
 * (TopicClusterFilter) — one cache entry per lens.
 */
const getTopicClusteringTree = async (topicClusteringLens) => {
  // Omitting the query param entirely tells the BE to auto-pick the
  // most-recently-updated lens for the tenant.
  const url = topicClusteringLens
    ? `/api/aiAnnotation/getTopicClusteringTree?topicClusteringLens=${encodeURIComponent(topicClusteringLens)}`
    : `/api/aiAnnotation/getTopicClusteringTree`;
  const response = await axios.get(url);
  return response.data;
};

// Omit topicClusteringLens to get the tenant's configured lens (ootils
// resolveTopicClusteringLens on the BE) — formerly the most-recently-updated
// tree (the BE auto-picks) — useful when the caller doesn't know the
// lens vocabulary. Pass a specific name to pin to that lens.
/**
 * GET /api/aiAnnotation/getJevDecisions?contentType=&docId= — what Jev decided
 * on one document in its latest jevassist run (fields, speakers, every
 * highlight's outcome with scores). { available: false } when the document
 * has no Jev run. Published page "Jev decisions" modal.
 */
const getJevDecisions = async ({ contentType, docId }) => {
  const response = await axios.get(`/api/aiAnnotation/getJevDecisions?contentType=${encodeURIComponent(contentType)}&docId=${encodeURIComponent(docId)}`);
  return response.data;
};

/** @param {{ contentType?: string, docId?: string, enabled?: boolean }} [params] */
export const useGetJevDecisions = ({ contentType, docId, enabled = true } = {}) =>
  useQuery({
    queryKey: ["jevDecisions", contentType, docId],
    queryFn: () => getJevDecisions({ contentType, docId }),
    enabled: enabled && !!contentType && !!docId,
    refetchOnWindowFocus: false,
  });

export const useGetTopicClusteringTree = ({ topicClusteringLens, enabled = true } = {}) =>
  useQuery({
    queryKey: ["topicClusteringTree", topicClusteringLens || "__auto__"],
    queryFn: () => getTopicClusteringTree(topicClusteringLens),
    enabled,
    refetchOnWindowFocus: false,
    retry: 0,
  });

/**
 * POST /api/aiAnnotation/getTopicClusteringTreeCounts { topicClusteringLens, activeFilters }.
 *
 * The tree as COUNTS within a filter scope — per node annotationCount,
 * signalCategoryCounts, datasets, majority signalId (sub-topics), topics'
 * parked counts, unplacedCount, and `scope` chip counts — with the filters
 * applied on the server (processActiveFilters: datesThatMatterType for the
 * period, valuePathType for dataset/voice, the annotation tags filter for
 * signals). No observation arrays; ~100KB where the full tree is ~30MB on a
 * large tenant. Used by the Topics V2 tab. Null when no recluster has run.
 */
const getTopicClusteringTreeCounts = ({ topicClusteringLens, activeFilters, withPriorityStats }) =>
  axios
    .post("/api/aiAnnotation/getTopicClusteringTreeCounts", { topicClusteringLens, activeFilters, withPriorityStats })
    .then((res) => res.data);

// `withPriorityStats` adds docs + voice per node (the Priorities field's
// severity inputs) — its own cache entry, so the cards' request is untouched.
export const useGetTopicClusteringTreeCounts = ({ topicClusteringLens, activeFilters = [], withPriorityStats = false, enabled = true } = {}) =>
  useQuery({
    queryKey: ["topicClusteringTreeCounts", topicClusteringLens || "__auto__", activeFilters, withPriorityStats],
    queryFn: () => getTopicClusteringTreeCounts({ topicClusteringLens, activeFilters, withPriorityStats }),
    enabled,
    refetchOnWindowFocus: false,
    retry: 0,
    // A scope already counted this session is served from cache and NOT
    // re-fetched in the background — the key is the filter set, so flipping
    // back to an earlier selection is instant.
    staleTime: 5 * 60 * 1000,
  });

/**
 * POST /api/aiAnnotation/AIAutoAnnotate.
 *
 * Accepted variables:
 *   contentType          (required)
 *   docIds               (optional) — annotate only these docs; omit to scope to the
 *                                     whole contentType
 *   valuePaths           (optional) — annotate only these fields; omit for all
 *                                     annotation-enabled fields on the tpl
 *   skipExisting         (default true) — skip docs/fields already annotated
 *   showLogs             (default false) — verbose response (used by playground)
 *   allowedSignalTagIds  (optional, staging/dev only) — Signals-picker
 *                                     allow-list; when present, annotator only
 *                                     considers these Signal tag _ids +
 *                                     inductive Signal creation is suppressed.
 *                                     BE returns 400 if this arrives on prod
 *                                     (feature: aiAnnotateSignalPicker).
 *   annotatorCall1Model  (optional, staging/dev only) — model spec for this
 *                                     run's Call 1 ({ provider, modelName,
 *                                     baseURL?, apiKeyEnv?, maxDocsPerGroup? });
 *                                     the tenant's configured model is not
 *                                     changed. Same prod gate as above.
 *
 * No banner gets popped automatically except on error — callers handle their
 * own success UX (banner / floating progress / etc.).
 */
export const useTriggerAIAutoAnnotate = ({ silenceErrorBanner = false } = {}) => {
  const { genErrorMsgBanners } = useGenErrorMsgBanners();

  return useMutation(
    ({
      contentType,
      docIds,
      valuePaths,
      skipExisting = true,
      showLogs = false,
      allowedSignalTagIds,
      annotatorCall1Model,
    }) =>
      axios.post("/api/aiAnnotation/AIAutoAnnotate", {
        contentType,
        ...(Array.isArray(docIds) && docIds.length > 0 ? { docIds } : {}),
        ...(Array.isArray(valuePaths) && valuePaths.length > 0
          ? { valuePaths }
          : {}),
        skipExisting,
        showLogs,
        ...(Array.isArray(allowedSignalTagIds) && allowedSignalTagIds.length > 0
          ? { allowedSignalTagIds }
          : {}),
        ...(annotatorCall1Model ? { annotatorCall1Model } : {}),
      }),
    {
      onError: (err) => {
        if (!silenceErrorBanner) genErrorMsgBanners({ err });
      },
    }
  );
};

/**
 * POST /api/content/wipeDocumentAnnotations/:contentType/:contentId.
 *
 * Wipes ALL annotations (human + AI) from a single published doc. The backend
 * strips annoData + Lexical marks off the doc and saves it through the normal
 * quickUpdate pipeline, which propagates the deletes to the standalone
 * annotations collection + Elasticsearch.
 *
 * Variables: { contentType, contentId }.
 *
 * Like useTriggerAIAutoAnnotate, no success banner is popped automatically —
 * callers handle their own success UX + cache invalidation.
 */
export const useWipeDocumentAnnotations = ({ silenceErrorBanner = false } = {}) => {
  const { genErrorMsgBanners } = useGenErrorMsgBanners();

  return useMutation(
    ({ contentType, contentId }) =>
      axios.post(
        `/api/content/wipeDocumentAnnotations/${contentType}/${contentId}`,
      ),
    {
      onError: (err) => {
        if (!silenceErrorBanner) genErrorMsgBanners({ err });
      },
    }
  );
};

/**
 * Runtime logs (SYSTEM_AIRuntimeLogs), any type.
 *
 * With `type`: GET /api/aiRuntimeLogs?type=<type> — every run of that type
 * (comma-separate for several), newest first, WITHOUT step logs (list
 * projection).
 * With `id`:   GET /api/aiRuntimeLogs/:id — that one run WITH its step logs
 * (e.g. the reconciliation audit trail the topic-clustering detail
 * expander renders).
 */
const getRuntimeLogs = async ({ type, id }) => {
  const url = id ? `/api/aiRuntimeLogs/${id}` : `/api/aiRuntimeLogs?type=${encodeURIComponent(type)}`;
  const response = await axios.get(url);
  return response.data;
};

export const useGetRuntimeLogs = ({ type, id } = {}) => {
  const wantsDetail = !type;
  return useQuery({
    queryKey: ["runtimeLogs", wantsDetail ? `detail:${id}` : `list:${type}`],
    queryFn: () => getRuntimeLogs({ type, id }),
    // Detail mode with no id yet (nothing expanded) must not fire a request.
    enabled: !wantsDetail || !!id,
    refetchOnWindowFocus: false,
  });
};

/**
 * POST /api/aiAnnotation/setTopicClusteringStability — the human "lock
 * topics" action. stable: true freezes the structure (new observations get
 * placed incrementally, the tree stops reorganizing); false unlocks (full
 * reclusters resume). Invalidates the tree readers — the full tree and the
 * counts payload (which carries `stable` too) — so the footer state and any
 * lock UI refresh immediately.
 */
export const useSetTopicClusteringStability = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ topicClusteringLens, stable }) =>
      axios.post("/api/aiAnnotation/setTopicClusteringStability", { topicClusteringLens, stable }),
    {
      onSuccess: (_res, { topicClusteringLens }) => {
        queryClient.invalidateQueries(["topicClusteringTree", topicClusteringLens]);
        queryClient.invalidateQueries(["topicClusteringTreeCounts"]);
      },
    }
  );
};

/**
 * POST /api/aiAnnotation/reclusterTopics { mode, force } — run a clustering
 * mode's lens NOW (zero-delay job, no 10-minute quiescence wait; `force`
 * runs even on an unchanged corpus). Off-prod tooling: the BE rejects
 * mode/force on prod (topicsReclusterControl). The job runs on okf-sub;
 * the tree readers are refreshed when the user next loads, not here.
 */
export const useReclusterTopics = () =>
  useMutation(({ mode, force = true }) => axios.post("/api/aiAnnotation/reclusterTopics", { mode, force }));

/**
 * POST /api/aiAnnotation/scorePriorities { topicClusteringLens, activeFilters, period, scoreGrain, trendWindow, compareKey }.
 *
 * Priorities for a scope and period, scored on the server: the counts
 * payload, the history tree, the scored { priorities, wins, meta } and the
 * per-sub-topic series. Null when no recluster has run. Every item carries
 * `momentumLabel` (the wording to print) and every priority `priorityOrder`.
 */
const scorePriorities = (body) => axios.post("/api/aiAnnotation/scorePriorities", body).then((res) => res.data);

export const useScorePriorities = ({ topicClusteringLens, activeFilters = [], period, scoreGrain, trendWindow, compareKey, enabled = true } = {}) =>
  useQuery({
    queryKey: ["scorePriorities", topicClusteringLens || "__auto__", activeFilters, period?.value, scoreGrain, trendWindow, compareKey],
    queryFn: () => scorePriorities({ topicClusteringLens, activeFilters, period, scoreGrain, trendWindow, compareKey }),
    enabled: enabled && !!period?.value,
    refetchOnWindowFocus: false,
    retry: 0,
    // A scope already scored this session is served from cache and NOT
    // re-fetched in the background — flipping back to an earlier selection
    // is instant.
    staleTime: 5 * 60 * 1000,
  });

/**
 * POST /api/aiAnnotation/getPriorityFindings { period, compareKey, topicClusteringLens, limit }.
 *
 * The narrated findings for a period: { findings: [{ id, kind, kindLabel,
 * headline, why, narrated, ... }], total, meta, narrationError }. The BE
 * caches the narration by a hash of the facts, so repeat reads of unchanged
 * data never reach the model.
 */
const getPriorityFindings = (body) => axios.post("/api/aiAnnotation/getPriorityFindings", body).then((res) => res.data);

export const useGetPriorityFindings = ({ period, compareKey, topicClusteringLens, limit, enabled = true } = {}) =>
  useQuery({
    queryKey: ["priorityFindings", topicClusteringLens || "__auto__", period?.value, compareKey, limit],
    queryFn: () => getPriorityFindings({ period, compareKey, topicClusteringLens, limit }),
    enabled: enabled && !!period?.value,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 0,
  });

/**
 * POST /api/aiAnnotation/getPriorityFindingEvidence { id, period, compareKey, topicClusteringLens }.
 *
 * The evidence behind one finding card: { found, tree, evidence } — the
 * rule, the rank terms, the scored subjects with their trajectories and
 * observations, the topic's mix per period, and the facts the narrator was
 * given. Re-derives on the server, so it is asked for on click only.
 */
const getPriorityFindingEvidence = (body) => axios.post("/api/aiAnnotation/getPriorityFindingEvidence", body).then((res) => res.data);

export const useGetPriorityFindingEvidence = ({ id, period, compareKey, topicClusteringLens, enabled = true } = {}) =>
  useQuery({
    queryKey: ["priorityFindingEvidence", topicClusteringLens || "__auto__", period?.value, compareKey, id],
    queryFn: () => getPriorityFindingEvidence({ id, period, compareKey, topicClusteringLens }),
    enabled: enabled && !!id && !!period?.value,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 0,
  });

/**
 * The insights agent — TEMPORARY, local-only doors while the run executes
 * inside the request (minutes). GET returns the latest stored run for a
 * period with `stale` when the data has moved on; POST runs the desk (or
 * returns the cached run) and refreshes the GET.
 */
const getInsightsAgentRun = (period) => axios.get("/api/aiAnnotation/insightsAgent/run", { params: { period } }).then((res) => res.data);

export const useGetInsightsAgentRun = ({ period, enabled = true } = {}) =>
  useQuery({
    queryKey: ["insightsAgentRun", period],
    queryFn: () => getInsightsAgentRun(period),
    enabled: enabled && !!period,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 0,
  });

/**
 * POST /api/aiAnnotation/insightsAgent/quickSummary { node, period, compareKey, activeFilters, topicClusteringLens, force }.
 *
 * The quick summary for a topic or sub-topic under the viewer's filters:
 * { bullets: [{ text, evidence: [{ id, quote }], numbers, source }], quoted,
 * thin, facts, sample, checks, cached }. Seconds on a miss (one small-model
 * call), instant when the server has one for the same brief. `period` is
 * the period KEY ("2026-09"); the period filter in activeFilters is
 * ignored by the server, so the panel's own list can go as it is.
 */
const getInsightsQuickSummary = (body) => axios.post("/api/aiAnnotation/insightsAgent/quickSummary", body).then((res) => res.data);

export const useGetInsightsQuickSummary = ({ node, period, compareKey = null, activeFilters = [], topicClusteringLens, force = false, enabled = true } = {}) =>
  useQuery({
    queryKey: ["insightsQuickSummary", node?.type, node?.id, period, compareKey, activeFilters, topicClusteringLens || "__auto__", force],
    queryFn: () => getInsightsQuickSummary({ node, period, compareKey, activeFilters, topicClusteringLens, force }),
    enabled: enabled && !!node?.id && !!period,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 0,
  });

export const useRunInsightsAgent = () => {
  const queryClient = useQueryClient();
  return useMutation(({ period, force = false }) => axios.post("/api/aiAnnotation/insightsAgent/run", { period, force }).then((res) => res.data), {
    onSuccess: (_data, { period }) => queryClient.invalidateQueries(["insightsAgentRun", period]),
  });
};
